import { Button, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext";
import { Stage } from "../../shared/CaseConfiguration";
import {
  endDateRange,
  IsoDate,
  showDate,
  stagesOnTimeline,
} from "../../shared/timeline";

/**
 * Enters the end a stage was expected to have, beside Today in the dialogs
 * that close it. There is none to enter where the timeline gives no expected
 * end, or one out of order with the other stages; one still to come shows
 * disabled, saying why.
 */
export const AsExpectedButton = ({
  stage,
  today,
  onPick,
}: {
  stage: Stage;
  today: IsoDate;
  onPick: (date: IsoDate) => void;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "case.timeline" });
  const caseContext = useCase();
  const { timeline } = caseContext.getCase();
  const stages = caseContext.getStages();
  const expected = stagesOnTimeline(timeline, stages, today).find(
    (row) => row.stage === stage,
  )?.expectedEnd;
  if (expected === undefined) return null;
  // a stage cannot have ended on a day still to come: it says so
  const toCome = expected > today;
  const { min, max } = endDateRange(timeline, stages, stage, today);
  if (!toCome && (expected > max || (min !== undefined && expected < min)))
    return null;
  return (
    <Tooltip
      title={
        toCome
          ? t("asExpectedToCome", { date: showDate(expected) })
          : showDate(expected)
      }
      describeChild
    >
      {/* a tooltip needs an element that fires events, which a disabled
          button does not */}
      <span>
        <Button
          variant="outlined"
          disabled={toCome}
          sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
          onClick={() => onPick(expected)}
        >
          {t("asExpected")}
        </Button>
      </span>
    </Tooltip>
  );
};
